import { useApolloClient } from '@apollo/client';
import { UserIcon } from '@assets/icons';
import { useAuthContext } from '@authentication/Authentication';
import { clearProxyAuth } from '@authentication/clearProxyAuth';
import { ExternalLink, InternalLink } from '@components/link';
import { Typography } from '@components/typography';
import { useGetCurrentUserQuery } from '@generated/graphql';
import { Divider, MenuItem, Stack, useTheme } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { navigate } from 'gatsby';
import { useState } from 'react';
import { HeaderMenu } from './HeaderMenu';

export const Profile = () => {
  const { palette } = useTheme();
  const { user, logout } = useAuthContext();
  const { data: currentUserData } = useGetCurrentUserQuery();
  const email = currentUserData?.getCurrentUser?.email || user?.email || '-';
  const displayName = currentUserData?.getCurrentUser?.name || user?.name || '-';

  const { organizations } = useOrganizationContext();
  const hasAgentStudioOrg = organizations?.some((org) => org.serviceOnly);
  const client = useApolloClient();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = () => setAnchorEl(null);

  const clearAllCookies = () => {
    if (typeof document === 'undefined') return;

    const cookies = document.cookie.split(';');

    cookies.forEach((cookie) => {
      const [name] = cookie.split('=');
      const trimmedName = name.trim();

      // Skip clearing the 'deckard' cookie
      if (trimmedName === 'deckard') return;

      document.cookie = `${trimmedName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${trimmedName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;

      const domainParts = window.location.hostname.split('.');
      if (domainParts.length > 1) {
        const rootDomain = domainParts.slice(-2).join('.');
        document.cookie = `${trimmedName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain};`;
      }
    });
  };

  const handleLogout = async () => {
    if (process.env.JEST_WORKER_ID !== undefined) {
      return;
    }
    handleClose();
    if (hasAgentStudioOrg) {
      await clearProxyAuth();
    }
    await client.clearStore();
    sessionStorage.clear();
    localStorage.clear();
    clearAllCookies();
    void logout();
    navigate('/');
  };

  return (
    <HeaderMenu
      icon={UserIcon}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      ariaLabel="Profile menu"
    >
      <Stack px={4}>
        <Typography variant="caption2" unselectable="on" style={{ userSelect: 'none' }}>
          {displayName}
        </Typography>
        <Typography variant="caption2" unselectable="on" style={{ userSelect: 'none' }}>
          {email}
        </Typography>
      </Stack>
      <Divider color={palette.menu.border} sx={{ my: 2 }} />
      <InternalLink to="/profile" unstyled>
        <MenuItem onClick={handleClose}>
          <Typography variant="body2">Profile</Typography>
        </MenuItem>
      </InternalLink>
      <ExternalLink href="https://rendered.ai/privacy-policy/" unstyled>
        <MenuItem onClick={handleClose}>
          <Typography variant="body2" color="text.primary">
            Legal
          </Typography>
        </MenuItem>
      </ExternalLink>
      <MenuItem onClick={handleLogout}>
        <Typography variant="body2" color="text.primary">
          Logout
        </Typography>
      </MenuItem>
    </HeaderMenu>
  );
};
